import { theme as antdTheme } from 'antd';

import {
  hireportColors,
  hireportDarkColors,
  hireportDarkSurfaces,
  hireportLightSurfaces,
  rgbaFromHex,
} from './colors';
import { hireportFontSizes } from './fontSizes';

import type { ColorScheme } from './colorScheme';
import type { FontSizesTokens } from './fontSizes';
import type { ThemeConfig } from 'antd';

/* -------------------------------------------------------------------------- */
/* Shared layout primitives                                                   */
/* All raw pixel/colour literals used across components live here so a single */
/* value (e.g. the default 16px padding) is never re-typed per component.     */
/* -------------------------------------------------------------------------- */

/** Foreground on brand fills — antd `colorTextLightSolid`. */
const TEXT_ON_BRAND = '#ffffff';
const TRANSPARENT = 'transparent';

/** Spacing scale (px) shared by control padding / margins. */
export const SPACE_XS = 4;
export const SPACE_SM = 8;
export const SPACE_MD_SM = 12;
export const SPACE_MD = 16;
export const SPACE_LG = 24;
export const SPACE_XL = 32;

/** Unified control heights (px) — buttons, inputs, selects, pickers all align. */
const CONTROL_HEIGHT = 40;
const CONTROL_HEIGHT_SM = 28;
const CONTROL_HEIGHT_LG = 48;

/** antd outline width for focus rings / danger shadows. */
const CONTROL_OUTLINE_WIDTH = 3;

/** Radius scale (px) shared by controls and tenant theme overrides. */
export const RADIUS_SM = 4;
export const RADIUS_MD = 8;
const RADIUS_LG = 12;

/* Modal widths (px) shared by modals. */
export const MODAL_WIDTH_SM = {
  sm: '92vw',
  lg: '40vw',
};
export const MODAL_WIDTH_MD = {
  sm: '92vw',
  lg: '60vw',
};
export const MODAL_WIDTH_LG = '92vw';

/* -------------------------------------------------------------------------- */
/* Theme model — palette keys match colors.ts; tenants spread partial overrides */
/* -------------------------------------------------------------------------- */

export type HirePortTheme = {
  themeName: string;
  colorPrimary: string;
  colorSecondary: string;
  colorText: string;
  colorThird: string;
  colorSubtle: string;
  colorMuted: string;
  colorFaint: string;
  colorBrightness: string;
  borderColor: string;
  controlBorderColor: string;
  colorItemSelectedBg: string;
  colorInfo: string;
  colorInfoBg: string;
  colorWarning: string;
  colorWarningHover: string;
  colorWarningActive: string;
  colorWarningBg: string;
  colorWarningBorder: string;
  colorError: string;
  colorErrorHover: string;
  colorErrorActive: string;
  colorErrorBg: string;
  colorErrorBgFilledHover: string;
  colorErrorBorder: string;
  colorPrimaryBg: string;
  colorBgContainer: string;
  colorBgElevated: string;
  backgroundColor: string;
  disabledSwitchColor: string;
  radius: number;
  radiusSmall: number;
  radiusLg: number;
  fontSizes: FontSizesTokens;
};

export const defaultTheme: HirePortTheme = {
  themeName: 'default',
  ...hireportColors,
  ...hireportLightSurfaces,
  colorPrimaryBg: hireportColors.colorItemSelectedBg,
  backgroundColor: 'transparent',
  disabledSwitchColor: hireportColors.colorMuted,
  radius: RADIUS_MD,
  radiusSmall: RADIUS_SM,
  radiusLg: RADIUS_LG,
  fontSizes: hireportFontSizes,
};

export const defaultDarkTheme: HirePortTheme = {
  ...defaultTheme,
  ...hireportDarkColors,
  ...hireportDarkSurfaces,
  colorPrimaryBg: hireportDarkColors.colorItemSelectedBg,
  disabledSwitchColor: hireportDarkColors.colorMuted,
};

/* -------------------------------------------------------------------------- */
/* Status (success / warning / error) map tokens                              */
/* -------------------------------------------------------------------------- */

type PaletteColors = Pick<
  HirePortTheme,
  | 'colorError'
  | 'colorErrorHover'
  | 'colorErrorActive'
  | 'colorErrorBg'
  | 'colorErrorBgFilledHover'
  | 'colorErrorBorder'
  | 'colorWarning'
  | 'colorWarningHover'
  | 'colorWarningActive'
  | 'colorWarningBg'
  | 'colorWarningBorder'
>;

/** Error map tokens — shared by global seed and the Button `danger` variant. */
function errorMapTokens(c: PaletteColors) {
  const errorOutline = rgbaFromHex(c.colorError, 0.1);

  return {
    colorError: c.colorError,
    colorErrorHover: c.colorErrorHover,
    colorErrorActive: c.colorErrorActive,
    colorErrorBg: c.colorErrorBg,
    colorErrorBgHover: c.colorErrorBg,
    colorErrorBgActive: c.colorErrorBg,
    colorErrorBgFilledHover: c.colorErrorBgFilledHover,
    colorErrorBorder: c.colorErrorBorder,
    colorErrorBorderHover: c.colorErrorHover,
    colorErrorText: c.colorError,
    colorErrorTextHover: c.colorErrorHover,
    colorErrorTextActive: c.colorErrorActive,
    colorErrorOutline: errorOutline,
  };
}

/** Warning map tokens — Alert/Typography/Tag caution states. */
function warningMapTokens(c: PaletteColors) {
  const warningOutline = rgbaFromHex(c.colorWarning, 0.1);

  return {
    colorWarning: c.colorWarning,
    colorWarningHover: c.colorWarningHover,
    colorWarningActive: c.colorWarningActive,
    colorWarningBg: c.colorWarningBg,
    colorWarningBgHover: c.colorWarningBg,
    colorWarningBorder: c.colorWarningBorder,
    colorWarningBorderHover: c.colorWarningHover,
    colorWarningText: c.colorWarning,
    colorWarningTextHover: c.colorWarningHover,
    colorWarningTextActive: c.colorWarningActive,
    colorWarningOutline: warningOutline,
  };
}

/* -------------------------------------------------------------------------- */
/* Theme builder                                                              */
/* -------------------------------------------------------------------------- */

/** Ant Design v6 theme derived from the merged HirePort theme object. */
export function buildAntdTheme(
  theme: HirePortTheme = defaultTheme,
  colorScheme: ColorScheme = 'light',
): ThemeConfig {
  const t = theme;
  const fs = t.fontSizes;
  const border = t.borderColor;
  const containerBg = t.colorBgContainer;

  /* Focus is a neutral dark-slate ring — brand green is for actions and
     checked states, not for every input interaction. */
  const focusOutline = rgbaFromHex(t.colorThird, 0.12);
  const focusRing = (color: string) =>
    `0 0 0 ${CONTROL_OUTLINE_WIDTH}px ${rgbaFromHex(color, 0.12)}`;
  /* Crisp focus ring instead of a blurred glow — reads as an outline. */
  const activeShadow = focusRing(t.colorThird);

  const statusShadows = {
    errorActiveShadow: focusRing(t.colorError),
    warningActiveShadow: focusRing(t.colorWarning),
  };

  const controlInteraction = {
    activeBorderColor: t.colorThird,
    /* Quiet slate hover; the darker slate is reserved for the focused state. */
    hoverBorderColor: t.colorFaint,
  };
  /* No padding overrides — heights derive from the global controlHeight scale
     so inputs, selects, pickers, and buttons are pixel-identical. */
  const textControl = {
    ...controlInteraction,
    activeShadow,
    ...statusShadows,
    borderRadiusSM: t.radius,
    borderRadiusLG: t.radius,
    inputFontSize: fs.fontSizeBase,
    inputFontSizeSM: fs.fontSizeSm,
    inputFontSizeLG: fs.fontSizeBase,
  };

  return {
    algorithm:
      colorScheme === 'dark'
        ? antdTheme.darkAlgorithm
        : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: t.colorPrimary,
      colorPrimaryHover: t.colorSecondary,
      colorPrimaryActive: t.colorThird,
      colorPrimaryBg: t.colorPrimaryBg,
      colorPrimaryText: TEXT_ON_BRAND,
      /* Links use the accessible emerald secondary and underline on hover for
         the clickable affordance. */
      colorLink: t.colorSecondary,
      colorLinkHover: t.colorSecondary,
      linkHoverDecoration: 'underline',
      /* One success green (same emerald as links) — brand-bright green is
         reserved for primary actions. */
      colorSuccess: t.colorSecondary,
      colorSuccessBg: t.colorItemSelectedBg,
      colorSuccessBorder: rgbaFromHex(t.colorSecondary, 0.25),
      colorText: t.colorText,
      colorTextSecondary: t.colorSubtle,
      colorTextTertiary: t.colorMuted,
      colorTextQuaternary: t.colorFaint,
      colorTextDisabled: t.colorFaint,
      /* Elevation: layout canvas, container cards/inputs, elevated panels. */
      colorBgLayout: t.colorBrightness,
      colorBgContainer: containerBg,
      colorBgElevated: t.colorBgElevated,
      colorBgSpotlight: t.colorThird,
      /* Controls get the darker outline; hairlines/dividers stay light. */
      colorBorder: t.controlBorderColor,
      colorBorderSecondary: border,
      colorSplit: border,
      /* Status colors */
      colorInfo: t.colorInfo,
      colorInfoBg: t.colorInfoBg,
      ...warningMapTokens(t),
      ...errorMapTokens(t),
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      /* Inter has real 500/600 — semibold for headings/emphasis, medium for
         buttons and labels-of-record; 700 is reserved for rare loud moments. */
      fontWeightStrong: 600,
      fontSize: fs.fontSizeBase,
      fontSizeSM: fs.fontSizeSm,
      fontSizeLG: fs.fontSizeLg,
      /* Compressed heading scale (28/24/20/16/14): page titles lead, section
         titles stay close to body size so data outranks chrome. */
      fontSizeHeading1: fs.fontSize4xl,
      fontSizeHeading2: fs.fontSize3xl,
      fontSizeHeading3: fs.fontSize2xl,
      fontSizeHeading4: fs.fontSizeLg,
      fontSizeHeading5: fs.fontSizeBase,
      lineHeight: 1.55,
      lineHeightHeading1: 1.25,
      lineHeightHeading2: 1.25,
      lineHeightHeading3: 1.3,
      lineHeightHeading4: 1.4,
      lineHeightHeading5: 1.45,
      borderRadius: t.radius,
      borderRadiusSM: t.radiusSmall,
      borderRadiusLG: t.radiusLg,
      paddingXXS: SPACE_XS,
      paddingXS: SPACE_SM,
      padding: SPACE_MD,
      paddingLG: SPACE_LG,
      sizeXL: SPACE_XL,
      /* One control-height scale for the whole platform — every control in a
         row (button, input, select, picker) is exactly the same height. */
      controlHeight: CONTROL_HEIGHT,
      controlHeightSM: CONTROL_HEIGHT_SM,
      controlHeightLG: CONTROL_HEIGHT_LG,
      controlOutline: focusOutline,
      controlOutlineWidth: CONTROL_OUTLINE_WIDTH,
    },
    cssVar: {},
    components: {
      Alert: {
        fontSizeLG: fs.fontSizeBase,
        colorText: t.colorSubtle,
        marginXS: 0,
        withDescriptionPadding: SPACE_MD,
        borderRadius: t.radius,
        borderRadiusLG: t.radius,
      },
      Avatar: {
        containerSize: 40,
        containerSizeLG: 62,
        containerSizeSM: CONTROL_HEIGHT_SM,
        textFontSizeSM: fs.fontSizeSm,
        textFontSizeLG: fs.fontSizeXl,
      },
      Badge: {
        textFontSize: fs.fontSizeSm,
        textFontSizeSM: fs.fontSizeSm,
        textFontWeight: 500,
      },
      Button: {
        fontWeight: 500,
        /* Flat buttons — antd's default drop shadows (incl. the red glow under
           danger buttons) read as noise on a clean surface. */
        defaultShadow: 'none',
        primaryShadow: 'none',
        dangerShadow: 'none',
        contentFontSize: fs.fontSizeBase,
        contentFontSizeLG: fs.fontSizeLg,
        contentFontSizeSM: fs.fontSizeSm,
        onlyIconSizeSM: fs.fontSizeSm,
        onlyIconSize: fs.fontSizeLg,
        onlyIconSizeLG: fs.fontSizeXl,
        paddingInline: SPACE_MD,
        paddingInlineLG: SPACE_LG,
        paddingInlineSM: SPACE_MD_SM,

        controlItemBgActiveHover: t.colorPrimary,
        colorPrimaryBgHover: t.colorPrimary,
        colorPrimaryTextHover: TEXT_ON_BRAND,

        /* Default buttons hover neutral (slate border + gray wash), not green —
           green hover is only for primary/brand buttons. */
        defaultHoverBg: t.colorBrightness,
        defaultHoverBorderColor: t.colorFaint,
        defaultHoverColor: t.colorText,
        defaultActiveBg: t.colorBrightness,
        defaultActiveBorderColor: t.colorMuted,
        defaultActiveColor: t.colorText,
      },
      Card: {
        colorBorderSecondary: border,
        boxShadowTertiary: 'none',
        bodyPadding: SPACE_MD,
      },
      Calendar: {
        fullBg: TRANSPARENT,
        fullPanelBg: TRANSPARENT,
        fontSize: fs.fontSizeBase,
        miniContentHeight: 'auto',
      },
      Collapse: {
        fontSize: fs.fontSizeLg,
        fontSizeSM: fs.fontSizeSm,
        borderlessContentPadding: '0 0 0 0',
        headerBg: TRANSPARENT,
        contentBg: TRANSPARENT,
      },
      Divider: {
        colorSplit: border,
        margin: SPACE_MD,
        marginSM: SPACE_SM,
        marginLG: SPACE_LG,
      },
      Drawer: {
        colorBgElevated: t.colorBrightness,
        zIndexPopup: 1000,
        footerPaddingBlock: 10,
        footerPaddingInline: SPACE_MD,
        fontSizeHeading1: fs.fontSize3xl,
      },
      Modal: {
        colorBgElevated: t.colorBrightness,
        headerBg: TRANSPARENT,
        footerBg: TRANSPARENT,
        titleColor: t.colorText,
        titleFontSize: fs.fontSize2xl,
        titleLineHeight: 1.3,
      },
      Form: {
        /* Form labels speak the same caption language as data labels
           (IconLabelText / Label): small, medium-weight, a step lighter than
           the value. Weight comes from index.css (antd has no label token). */
        labelFontSize: fs.fontSizeSm,
        labelColor: t.colorSubtle,
        labelHeight: 22,
        itemMarginBottom: SPACE_LG,
        verticalLabelPadding: `0 0 ${SPACE_XS + 2}px`,
        labelRequiredMarkColor: t.colorError,
      },
      Input: {
        ...textControl,
        colorBgContainerDisabled: t.colorBrightness,
        colorTextDisabled: t.disabledSwitchColor,
        colorTextPlaceholder: t.colorFaint,
      },
      InputNumber: {
        ...textControl,
        colorBgContainerDisabled: t.colorBrightness,
      },
      DatePicker: {
        ...textControl,
        cellActiveWithRangeBg: rgbaFromHex(t.colorPrimary, 0.1),
        cellHoverWithRangeBg: rgbaFromHex(t.colorPrimary, 0.1),
        cellHoverBg: rgbaFromHex(t.colorPrimary, 0.15),
        cellRangeBorderColor: t.colorPrimary,
        cellBgDisabled: t.colorBrightness,
        zIndexPopup: 9999,
      },
      Select: {
        ...textControl,
        activeOutlineColor: focusOutline,
        optionSelectedBg: rgbaFromHex(t.colorPrimary, 0.15),
        /* Selected option keeps dark text on the tinted bg — readable. */
        optionSelectedColor: t.colorText,
        optionActiveBg: t.colorBrightness,
        colorTextPlaceholder: t.colorFaint,

        multipleItemHeight: 24,
        multipleItemHeightSM: 20,
        multipleItemHeightLG: 28,

        optionFontSize: fs.fontSizeSm,

        optionHeight: 32,
        optionPadding: SPACE_SM,
      },
      Switch: {
        fontSize: fs.fontSizeSm,
      },
      Layout: {
        bodyBg: t.colorBrightness,
        headerBg: containerBg,
        siderBg: TRANSPARENT,
        lightSiderBg: containerBg,
        triggerBg: t.colorPrimaryBg,
        lightTriggerBg: t.colorPrimaryBg,
        triggerColor: TEXT_ON_BRAND,
        triggerHeight: 40,
        zeroTriggerWidth: 40,
      },
      List: {
        itemPadding: `${SPACE_MD}px ${SPACE_MD}px`,
      },
      Popover: {
        zIndexPopup: 1050,
      },
      Checkbox: {
        fontSizeSM: fs.fontSizeSm,
        fontSize: fs.fontSizeBase,
      },
      Radio: {
        fontSizeSM: fs.fontSizeSm,
        fontSize: fs.fontSizeBase,
        dotSize: 8,
        radioSize: 18,
        buttonBg: t.colorBrightness,
        buttonColor: t.colorText,
        buttonSolidCheckedBg: t.colorPrimary,
        buttonSolidCheckedColor: TEXT_ON_BRAND,
        buttonSolidCheckedHoverBg: t.colorSecondary,
        buttonSolidCheckedActiveBg: t.colorThird,
      },
      Segmented: {
        trackBg: t.colorBrightness,
        trackPadding: SPACE_XS,
        itemColor: t.colorText,
        itemHoverBg: t.colorThird,
        itemHoverColor: TEXT_ON_BRAND,
        itemActiveBg: t.colorThird,
        itemSelectedBg: t.colorPrimary,
        itemSelectedColor: TEXT_ON_BRAND,
        fontSizeSM: fs.fontSizeSm,
      },
      Steps: {
        dotSize: 16,
      },
      Table: {
        borderColor: border,
        /* antd maps headerBorderRadius to tableRadius — header and container
           share the card radius so the table reads as one rounded surface. */
        headerBorderRadius: t.radius,
        headerColor: t.colorMuted,
        headerBg: containerBg,
        rowHoverBg: t.colorItemSelectedBg,
        /* Sorted columns stay quiet — no tinted body/header cells. */
        bodySortBg: containerBg,
        headerSortActiveBg: containerBg,
        headerSortHoverBg: containerBg,
        /* No vertical hairlines between header cells. */
        headerSplitColor: TRANSPARENT,
        /* Small tables keep body-size text — density comes from padding only. */
        cellFontSizeSM: fs.fontSizeSm,
        cellFontSize: fs.fontSizeSm,
        cellFontSizeMD: fs.fontSizeSm,
        colorSplit: rgbaFromHex(t.borderColor, 0.3),
        cellPaddingBlock: SPACE_MD,
        cellPaddingInline: SPACE_SM,
      },
      Tabs: {
        colorBorderSecondary: border,
        /* Quiet tab bar: muted labels, dark selected label, green ink bar as
           the only brand accent — tabs stay chrome, headings stay content. */
        inkBarColor: t.colorPrimary,
        itemColor: t.colorMuted,
        itemHoverColor: t.colorText,
        itemSelectedColor: t.colorText,
        itemActiveColor: t.colorText,
        titleFontSize: fs.fontSizeBase,
        titleFontSizeSM: fs.fontSizeSm,
        horizontalMargin: `0 0 ${SPACE_LG}px 0`,
        horizontalItemGutter: SPACE_LG,
      },
      Tag: {
        defaultBg: t.colorBrightness,
        defaultColor: t.colorSubtle,
        colorBorder: t.colorBrightness,
      },
      TreeSelect: {
        titleHeight: CONTROL_HEIGHT_SM,
      },
      Typography: {
        titleMarginTop: 0,
        titleMarginBottom: 0,
      },
      Tooltip: {
        controlHeight: CONTROL_HEIGHT_SM,
      },
      Rate: {
        starBg: t.colorFaint,
        starColor: t.colorPrimary,
        marginXS: 2,
      },
      Progress: {
        defaultColor: t.colorSecondary,
      },
    },
  };
}
